import {
  initI18n,
  DEFAULT_ARDOQ_I18NLOCALE,
  type I18nLocale,
} from '@ardoq/i18n';
import { ALL_LANGUAGE_CODES, DEFAULT_LANGUAGE } from 'supportedLanguages';
import { writePreAppLanguageCookie } from './preAppLanguageCookie';

const ORG_LANGUAGE_ENDPOINT = '/api/organization-language';
const ORG_LANGUAGE_FETCH_TIMEOUT_MS = 3000;

const SUPPORTED_LANGUAGE_VALUES = new Set(ALL_LANGUAGE_CODES);

let resolvedLocale: I18nLocale = DEFAULT_ARDOQ_I18NLOCALE;

const isSupportedLanguage = (value: unknown): value is string =>
  typeof value === 'string' && SUPPORTED_LANGUAGE_VALUES.has(value);

export const fetchOrgDefaultLanguage = async (): Promise<string> => {
  const controller = new AbortController();
  const timer = setTimeout(
    () => controller.abort(),
    ORG_LANGUAGE_FETCH_TIMEOUT_MS
  );
  try {
    const response = await fetch(ORG_LANGUAGE_ENDPOINT, {
      credentials: 'omit',
      signal: controller.signal,
    });
    if (!response.ok) return DEFAULT_LANGUAGE;
    const body: unknown = await response.json();
    if (typeof body !== 'object' || body === null) return DEFAULT_LANGUAGE;
    const language = (body as { language?: unknown }).language;
    return isSupportedLanguage(language) ? language : DEFAULT_LANGUAGE;
  } catch {
    return DEFAULT_LANGUAGE;
  } finally {
    clearTimeout(timer);
  }
};

/**
 * Returns the locale bundle resolved by `bootstrapPreAppI18n`.
 *
 * Pre-app screens (404, login, missing-org, ip-blocked, choose-org) render
 * before the authenticated `i18nLocale$` stream is populated. They call this
 * synchronously after the entry point has awaited `bootstrapPreAppI18n`, so
 * the bundle reflects the org-default language returned by the unauthenticated
 * `GET /api/organization-language` endpoint.
 *
 * Falls back to the empty English-default bundle when bootstrap has not run
 * (e.g. tests) or when the endpoint failed.
 */
export const getPreAppI18nLocale = (): I18nLocale => resolvedLocale;

/**
 * Fetch the org-default language and initialise i18next before the first
 * render of any unauthenticated entry point. Awaiting this before rendering
 * ensures `getPreAppI18nLocale()` and the `react-i18next` context return the
 * correct locale on the first paint, with English as a graceful fallback when
 * the endpoint is unreachable or the subdomain is unknown.
 *
 * The slim locale bundles are loaded via dynamic `import()` so they are
 * placed in their own chunk and stay out of the static graph of
 * authenticated entry points (e.g. `perspectiveEditor`), which must not
 * statically reach modules under `packages/*-interface/`.
 */
export const bootstrapPreAppI18n = async (): Promise<void> => {
  try {
    const [language, { PRE_APP_LOCALE_BUNDLES }] = await Promise.all([
      fetchOrgDefaultLanguage(),
      import('./preAppLocaleBundles'),
    ]);
    resolvedLocale =
      PRE_APP_LOCALE_BUNDLES[language] ?? DEFAULT_ARDOQ_I18NLOCALE;
    writePreAppLanguageCookie(language);
    await initI18n({ lng: language, resources: PRE_APP_LOCALE_BUNDLES });
  } catch {
    resolvedLocale = DEFAULT_ARDOQ_I18NLOCALE;
    writePreAppLanguageCookie(DEFAULT_LANGUAGE);
  }
};
